import { Parser } from 'htmlparser2';
import { PrizeV2 } from 'types/Prize';

export interface IHTMLParseSection {
  sectionHeader: string;
  /** Whether this is the "default" section, i.e the section where any bundles would appear */
  isDefaultSection?: boolean;
  content: IHTMLParseSectionContent[];
}

interface IHTMLParseSectionContent {
  tag: string;
  content: string;
}

export function parsePrizeHtmlSections(
  prize:
    | PrizeV2
    | {
        whatsIncludedBody: string;
        whatsIncludedHeading?: string;
      },
): { sections: IHTMLParseSection[]; attributes: string[] } {
  const html = prize.whatsIncludedBody;
  const sections: IHTMLParseSection[] = [];
  let currentSection: IHTMLParseSection | null = null;
  const openTags: string[] = [];

  const parser = new Parser(
    {
      onopentag: function (name, attribs) {
        if (name === 'h3') {
          if (currentSection !== null) {
            sections.push(currentSection);
          }
          currentSection = {
            sectionHeader: '',
            content: [],
          };
        } else if (
          ['ul', 'ol', 'p', 'li'].includes(name) &&
          currentSection !== null
        ) {
          if (openTags.length === 0 || name === 'p') {
            currentSection.content.push({ tag: name, content: `<${name}>` });
          } else {
            const lastElement =
              currentSection.content[currentSection.content.length - 1];
            lastElement.content += `<${name}>`;
          }
          openTags.push(name);
        }
      },
      ontext: function (text) {
        // The original implementation of this discards any text before the first H3. Not ideal, but if we change this
        // now it will cause the text to appear for any prizes that have the discarded text before a H3
        if (currentSection === null) return;

        if (currentSection.sectionHeader === '') {
          currentSection.sectionHeader =
            text.toLowerCase().indexOf('in a nutshell') === 0
              ? prize.whatsIncludedHeading || "What's Included?"
              : toTitleCase(
                  text.replace('&nbsp;', '').replace('&amp;', '').trim(),
                );

          // Ensure that the heading labelled as what's included is treated as the 'default' section
          if (
            currentSection.sectionHeader === prize.whatsIncludedHeading ||
            currentSection.sectionHeader === "What's Included?" ||
            currentSection.sectionHeader === "What's Included"
          ) {
            currentSection.isDefaultSection = true;
          }
        } else if (currentSection.content.length > 0) {
          const lastElement =
            currentSection.content[currentSection.content.length - 1];
          lastElement.content += text;
        }
      },
      onclosetag: function (name) {
        if (['h3', 'ul', 'ol', 'p', 'li'].includes(name)) {
          if (currentSection && currentSection.content.length > 0) {
            const lastElement =
              currentSection.content[currentSection.content.length - 1];
            lastElement.content += `</${name}>`;
            openTags.pop();
            if (openTags.length === 0 || name === 'p') {
              currentSection.content.push({ tag: name, content: '' });
            }
          }
        }
        if (name === 'html') {
          if (currentSection !== null) {
            sections.push(currentSection);
            currentSection = null;
          }
        }
      },
    },
    { decodeEntities: true },
  );

  parser.write(html || '');
  parser.end();

  // Ensure the last section is added
  if (currentSection !== null) {
    sections.push(currentSection);
  }

  if (sections.length == 0) {
    sections.push({
      sectionHeader: prize.whatsIncludedHeading || "What's Included?",
      isDefaultSection: true,
      content: [
        {
          tag: 'div',
          content: prize.whatsIncludedBody,
        },
      ],
    });
  }

  const attributesSection = sections.find(
    (x) => x.sectionHeader.toLowerCase() == 'attributes',
  );

  return {
    sections: sections.filter(
      (x) => x.sectionHeader.toLowerCase() != 'attributes',
    ),
    attributes:
      attributesSection?.content
        .map((x) => x.content.replace(/<[^>]+>/g, '').trim())
        .filter((x) => x) || [],
  };
}

function toTitleCase(text: string): string {
  return text
    .toLowerCase()
    .replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.slice(1));
}
